import React, { useEffect, useMemo, useState } from 'react';

import { RiAlertLine } from '@remixicon/react';

import { Button } from '@/design-system/button';
import { Field } from '@/design-system/field';
import { FormModal } from '@/design-system/form-modal';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import { SearchSelect } from '@/design-system/search-select';
import { Textarea } from '@/design-system/textarea';
import { ujpAPI, type UjpReason } from '@/services/api/ujp';

import { UJP_COPY } from '../copy';

/**
 * The two destructive decisions on a UJP, in one `sm` danger modal.
 *
 * - `reject` — a reason from `GET /v1/reasons?type=UJP_REJECTION` is required
 *   and "Lainnya" additionally requires a note, so the requester always gets
 *   something actionable back (DD3 / §14.3). "Tolak UJP" stays disabled until
 *   both hold.
 * - `cancel` — a plain confirm, requester only.
 *
 * The modal never talks to the decision endpoint itself: it hands the payload
 * back through `onConfirm` so the panel owns the request, the 409 handling and
 * the refetch in one place.
 */

export type UjpDecisionMode = 'reject' | 'cancel';

/** "Lainnya" (and only it) demands a note; matched on the seeded code first. */
const needsNote = (reason?: UjpReason): boolean =>
  !!reason && (reason.code === 'LAINNYA' || /^\s*(lain|other)/i.test(reason.label));

/**
 * Reject-mode wording overrides (CR-7): the biaya tambahan reject reuses this
 * modal, same reason picker and LAINNYA rule, under its own title / CTA.
 */
export interface UjpDecisionModalLabels {
  title?: string;
  subtitle?: string;
  cta?: string;
}

export interface UjpDecisionModalProps {
  mode: UjpDecisionMode | null;
  referenceId: string;
  open: boolean;
  submitting?: boolean;
  labels?: UjpDecisionModalLabels;
  onClose: () => void;
  /** reject → `{ reasonCode, note }`; cancel → no argument. */
  onConfirm: (payload?: { reasonCode: string; note?: string }) => void;
}

const UjpDecisionModal: React.FC<UjpDecisionModalProps> = ({
  mode,
  referenceId,
  open,
  submitting = false,
  labels,
  onClose,
  onConfirm,
}) => {
  const c = UJP_COPY.panel;
  const rejectTitle = labels?.title ?? c.rejectTitle;
  const rejectSubtitle = labels?.subtitle ?? c.rejectSubtitle;
  const rejectCta = labels?.cta ?? c.rejectTitle;
  const [reasons, setReasons] = useState<UjpReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [reasonsError, setReasonsError] = useState<string | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (!open) {
      setReasonCode('');
      setNote('');
      return;
    }
    if (mode !== 'reject') return;
    let cancelled = false;
    setLoadingReasons(true);
    setReasonsError(null);
    ujpAPI
      .listRejectionReasons()
      .then((rows) => {
        if (!cancelled) setReasons(rows);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setReasons([]);
        setReasonsError(err instanceof Error ? err.message : c.reasonsEmpty);
      })
      .finally(() => {
        if (!cancelled) setLoadingReasons(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mode, c.reasonsEmpty]);

  const selected = useMemo(
    () => reasons.find((r) => r.code === reasonCode),
    [reasons, reasonCode]
  );
  const noteRequired = needsNote(selected);
  const noteMissing = noteRequired && !note.trim();
  const canReject = !!reasonCode && !noteMissing && !submitting;

  if (!mode) return null;

  if (mode === 'cancel') {
    return (
      <FormModal
        open={open}
        onClose={submitting ? () => undefined : onClose}
        icon={<RiAlertLine />}
        iconTone="danger"
        title={c.cancelConfirmTitle(referenceId)}
        subtitle={c.cancelBody}
        size="sm"
        closeOnBackdropClick={!submitting}
        closeOnEscape={!submitting}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              tone="neutral"
              style="stroke"
              onClick={onClose}
              disabled={submitting}
            >
              {c.cancelBack}
            </Button>
            <Button
              tone="destructive"
              onClick={() => onConfirm()}
              loading={submitting}
              disabled={submitting}
            >
              {c.cancel}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-text-sub-600">{c.cancelHint}</p>
      </FormModal>
    );
  }

  return (
    <FormModal
      open={open}
      onClose={submitting ? () => undefined : onClose}
      icon={<RiAlertLine />}
      iconTone="danger"
      title={`${rejectTitle} ${referenceId}`}
      subtitle={rejectSubtitle}
      size="sm"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={submitting}
          >
            {UJP_COPY.wizard.cancel}
          </Button>
          <Button
            tone="destructive"
            onClick={() => onConfirm({ reasonCode, note: note.trim() || undefined })}
            disabled={!canReject}
            loading={submitting}
          >
            {rejectCta}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field>
          {/*
            The kit SearchSelect takes no `id`, so the label cannot be wired
            with htmlFor; a labelled group around it gives assistive tech (and
            the tests) the same association.
          */}
          <Label id="ujp-reject-reason-label" required>
            {c.reason}
          </Label>
          <div role="group" aria-labelledby="ujp-reject-reason-label">
            <SearchSelect
              options={reasons.map((r) => ({ value: r.code, label: r.label }))}
              value={reasonCode || undefined}
              onChange={(v) => setReasonCode(String(v))}
              placeholder={c.reasonPlaceholder}
              emptyMessage={reasonsError ?? c.reasonsEmpty}
              loading={loadingReasons}
              disabled={submitting}
              className="w-full"
            />
          </div>
          <Hint tone={reasonsError ? 'error' : 'neutral'}>
            {reasonsError ?? c.reasonHint}
          </Hint>
        </Field>

        <Field>
          <Label htmlFor="ujp-reject-note" required={noteRequired}>
            {noteRequired ? c.note : c.noteOptional}
          </Label>
          <Textarea
            id="ujp-reject-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={c.notePlaceholder}
            disabled={submitting}
            className="resize-none"
          />
          {noteMissing && <Hint tone="error">{c.noteRequired}</Hint>}
        </Field>
      </div>
    </FormModal>
  );
};

export default UjpDecisionModal;
